/**
 * Store for managing parade lineup data
 *
 * IMPORTANT NOTES:
 * 1. Lineup data is stored per parade year
 * 2. Zones are alphabetically ordered and non-reorderable
 * 3. Parade order numbers are calculated across all zones
 * 4. Real-time updates via Firestore subscriptions
 */
import type { DocumentData, Timestamp } from 'firebase/firestore'
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getFirestore,
  onSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp as FirestoreTimestamp,
  updateDoc,
} from 'firebase/firestore'
import { defineStore } from 'pinia'
import type { LiveSpec } from '~/utils/liveData'
import { holdLive, liveDocListener, liveQueryListener } from '~/utils/liveData'
import { memberShouldPayDues } from '~/composables/useShouldPayDues.js'
import { isApprovedForParade } from '~/shared/utils/subkreweApproval.js'
import type {
  Lineup,
  LineupLinedUpStatus,
  Zone,
  ZoneAssignment,
} from '~/shared/types/lineup.js'

export const useLineupStore = defineStore('lineup', {
  state: () => ({
    // Current lineup document (keyed by year)
    lineups: {} as Record<number, Lineup>,
    // Lined-up status by year and subkrewe
    linedUpStatus: {} as Record<number, Record<string, LineupLinedUpStatus>>,
    loading: false,
    initialized: false,
    // Years whose lineup document has answered once (it may not exist), so a
    // page can tell "still loading" from "no lineup"
    lineupLoaded: {} as Record<number, boolean>,
  }),

  getters: {
    /**
     * Get lineup for a specific year
     */
    getLineupByYear(): (year: number) => Lineup | undefined {
      return (year: number) => this.lineups[year]
    },

    /**
     * Check if lineup is published for a year
     */
    isPublished(): (year: number) => boolean {
      return (year: number) => {
        const lineup = this.lineups[year]
        return !!lineup?.publishedAt
      }
    },

    /**
     * Get all zones for a year, sorted alphabetically
     */
    getZones(): (year: number) => Zone[] {
      return (year: number) => {
        const lineup = this.lineups[year]
        if (!lineup) return []
        return [...lineup.zones].sort((a, b) => a.name.localeCompare(b.name))
      }
    },

    /**
     * Get zone by ID for a specific year
     */
    getZoneById(): (year: number, zoneId: string) => Zone | undefined {
      return (year: number, zoneId: string) => {
        const lineup = this.lineups[year]
        return lineup?.zones.find(z => z.id === zoneId)
      }
    },

    /**
     * Get all subkrewes assigned to any zone, with their parade order
     */
    getAssignedSubkrewes(): (year: number) => ZoneAssignment[] {
      return (year: number) => {
        const lineup = this.lineups[year]
        if (!lineup) return []

        const assignments: ZoneAssignment[] = []
        lineup.zones.forEach((zone) => {
          assignments.push(...zone.assignedSubKrewes)
        })

        return assignments.sort((a, b) => a.paradeOrder - b.paradeOrder)
      }
    },

    /**
     * Get unassigned subkrewes (not in any zone)
     * Only returns active and approved subkrewes
     */
    getUnassignedSubkrewes(): (year: number) => SubKrewe[] {
      return (year: number) => {
        const subkrewesStore = useSubKrewesStore()
        const assignedIds = new Set(
          this.getAssignedSubkrewes(year).map(a => a.subkreweId),
        )

        return subkrewesStore.subkrewesArray.filter((sk) => {
          // Must be approved for this parade (bands are approved per year)
          if (!isApprovedForParade(sk, year)) return false

          // Must not be assigned to a zone
          if (assignedIds.has(sk.id)) return false

          // Must not be abandoned
          if (sk.abandoned) return false

          // Support subkrewes don't roll in the parade
          if (sk.specialty?.includes('support')) return false

          // Internal subkrewes are always active (don't need captains)
          if (sk.specialty?.includes('internal')) return true

          // Must be active (has captains and at least one doesn't pay dues)
          const hasCaptains = sk.captains && sk.captains.length > 0
          if (!hasCaptains) return false

          // Check if at least one captain is active (doesn't have to pay dues)
          return sk.captains.some(captain => !memberShouldPayDues(captain))
        })
      }
    },

    /**
     * Get zone for a specific subkrewe
     */
    getZoneBySubKrewe(): (year: number, subkreweId: string) => Zone | undefined {
      return (year: number, subkreweId: string) => {
        const lineup = this.lineups[year]
        if (!lineup) return undefined

        return lineup.zones.find(zone =>
          zone.assignedSubKrewes.some(a => a.subkreweId === subkreweId),
        )
      }
    },

    /**
     * Get parade order for a subkrewe
     */
    getParadeOrder(): (year: number, subkreweId: string) => number | undefined {
      return (year: number, subkreweId: string) => {
        const lineup = this.lineups[year]
        if (!lineup) return undefined

        for (const zone of lineup.zones) {
          const assignment = zone.assignedSubKrewes.find(
            a => a.subkreweId === subkreweId,
          )
          if (assignment) return assignment.paradeOrder
        }
        return undefined
      }
    },

    /**
     * Check if subkrewe is lined up
     */
    isSubKreweLinedUp(): (year: number, subkreweId: string) => boolean {
      return (year: number, subkreweId: string) => {
        return this.linedUpStatus[year]?.[subkreweId]?.linedUp || false
      }
    },

    /**
     * Get lined-up status for a subkrewe
     */
    getLinedUpStatus(): (year: number, subkreweId: string) => LineupLinedUpStatus | undefined {
      return (year: number, subkreweId: string) => {
        return this.linedUpStatus[year]?.[subkreweId]
      }
    },

    /**
     * Get zones assigned to a lieutenant
     */
    getZonesForLieutenant(): (year: number, lieutenantId: string) => Zone[] {
      return (year: number, lieutenantId: string) => {
        const lineup = this.lineups[year]
        if (!lineup) return []

        return lineup.zones.filter((zone) => {
          const assignments = lineup.lieutenantAssignments[zone.id] || []
          return assignments.some(a => a.memberId === lieutenantId)
        })
      }
    },

    /**
     * Get radio exclusions for a year
     */
    getRadioExclusions(): (year: number) => Set<string> {
      return (year: number) => {
        const lineup = this.lineups[year]
        return new Set(lineup?.radioExclusions || [])
      }
    },

    /**
     * Get member count for a zone
     */
    getZoneMemberCount(): (year: number, zoneId: string) => number {
      return (year: number, zoneId: string) => {
        const zone = this.getZoneById(year, zoneId)
        if (!zone) return 0

        const subkrewesStore = useSubKrewesStore()
        let count = 0

        zone.assignedSubKrewes.forEach((assignment) => {
          const subkrewe = subkrewesStore.getSubkreweById(assignment.subkreweId)
          const actualCount = subkrewe?.members?.length || 0
          // For bands, use actual count if > 0, otherwise fall back to estimated_members
          // (older docs store the estimate as a string)
          if (subkrewe?.music === 'band' && actualCount === 0 && subkrewe.estimated_members) {
            count += Number(subkrewe.estimated_members) || 0
          }
          else {
            count += actualCount
          }
        })

        return count
      }
    },
  },

  actions: {
    /**
     * Create or update lineup document
     */
    async saveLineup(year: number, lineup: Partial<Lineup>): Promise<void> {
      const db = getFirestore()
      const lineupRef = doc(db, 'lineups', year.toString())

      const data: Record<string, unknown> = {
        ...lineup,
        year,
        updatedAt: serverTimestamp(),
      }

      // If creating new lineup, add createdAt
      const existing = await getDoc(lineupRef)
      if (!existing.exists()) {
        data.createdAt = serverTimestamp()
        data.publishedAt = null
        data.zones = lineup.zones || []
        data.lieutenantAssignments = lineup.lieutenantAssignments || {}
        data.lineupDescription = lineup.lineupDescription || ''
      }

      await setDoc(lineupRef, data, { merge: true })
    },

    /**
     * Create multiple zones at once (bulk creation)
     */
    async createZones(
      year: number,
      zones: Omit<Zone, 'id' | 'assignedSubKrewes'>[],
    ): Promise<void> {
      const db = getFirestore()
      const lineupRef = doc(db, 'lineups', year.toString())
      const existingDoc = await getDoc(lineupRef)

      const existingZones = existingDoc.exists()
        ? (existingDoc.data().zones || [])
        : []

      const newZones: Zone[] = zones.map(z => ({
        ...z,
        id: `zone-${z.name.toLowerCase().replace(/\s+/g, '-')}`,
        assignedSubKrewes: [],
      }))

      await this.saveLineup(year, {
        zones: [...existingZones, ...newZones],
      })
    },

    /**
     * Update a single zone
     */
    async updateZone(year: number, zoneId: string, updates: Partial<Zone>): Promise<void> {
      const lineup = this.lineups[year]
      if (!lineup) return

      const zoneIndex = lineup.zones.findIndex(z => z.id === zoneId)
      if (zoneIndex === -1) return

      const updatedZones = [...lineup.zones]
      updatedZones[zoneIndex] = { ...updatedZones[zoneIndex], ...updates } as Zone

      await this.saveLineup(year, { zones: updatedZones })
    },

    /**
     * Delete a zone (only if empty)
     */
    async deleteZone(year: number, zoneId: string): Promise<void> {
      const lineup = this.lineups[year]
      if (!lineup) return

      const zone = lineup.zones.find(z => z.id === zoneId)
      if (!zone || zone.assignedSubKrewes.length > 0) {
        throw new Error('Cannot delete zone with assigned subkrewes')
      }

      const updatedZones = lineup.zones.filter(z => z.id !== zoneId)
      await this.saveLineup(year, { zones: updatedZones })
    },

    /**
     * Delete entire lineup document for a year
     */
    async deleteLineup(year: number): Promise<void> {
      const db = getFirestore()
      const lineupRef = doc(db, 'lineups', year.toString())

      // Note: We don't delete linedUpStatus subcollection documents individually
      // because that would require list permission. Firebase will handle orphaned
      // subcollection documents, or we can clean them up via server-side functions.
      // For now, just delete the main document and clear local state.

      // Delete main lineup document
      await deleteDoc(lineupRef)

      // Clear from local state
      delete this.lineups[year]
      delete this.linedUpStatus[year]
    },

    /**
     * Assign subkrewe to zone with automatic parade order calculation
     */
    async assignSubKreweToZone(
      year: number,
      subkreweId: string,
      targetZoneId: string,
      insertPosition?: number,
    ): Promise<void> {
      const lineup = this.lineups[year]
      if (!lineup) return

      // Remove from current zone if assigned
      const updatedZones = lineup.zones.map((zone) => {
        return {
          ...zone,
          assignedSubKrewes: zone.assignedSubKrewes.filter(
            a => a.subkreweId !== subkreweId,
          ),
        }
      })

      // Find target zone
      const targetZoneIndex = updatedZones.findIndex(z => z.id === targetZoneId)
      if (targetZoneIndex === -1) return

      // Insert at position or append to end
      const targetZone = updatedZones[targetZoneIndex]
      if (!targetZone) return

      const position = insertPosition ?? targetZone.assignedSubKrewes.length

      targetZone.assignedSubKrewes.splice(position, 0, {
        subkreweId,
        paradeOrder: 0, // Will be recalculated
      })

      // Recalculate all parade orders
      this.recalculateParadeOrders(updatedZones)

      await this.saveLineup(year, { zones: updatedZones })
    },

    /**
     * Remove subkrewe from its zone (unassign it)
     */
    async removeSubKreweFromZone(
      year: number,
      subkreweId: string,
    ): Promise<void> {
      const lineup = this.lineups[year]
      if (!lineup) return

      // Remove from all zones
      const updatedZones = lineup.zones.map((zone) => {
        return {
          ...zone,
          assignedSubKrewes: zone.assignedSubKrewes.filter(
            a => a.subkreweId !== subkreweId,
          ),
        }
      })

      // Recalculate all parade orders
      this.recalculateParadeOrders(updatedZones)

      await this.saveLineup(year, { zones: updatedZones })
    },

    /**
     * Reorder subkrewes within or across zones
     */
    async reorderSubKrewes(
      year: number,
      subkreweId: string,
      targetZoneId: string,
      newPosition: number,
    ): Promise<void> {
      await this.assignSubKreweToZone(year, subkreweId, targetZoneId, newPosition)
    },

    /**
     * Recalculate parade-wide order numbers
     * Zones are processed alphabetically, subkrewes within zones by their array position
     */
    recalculateParadeOrders(zones: Zone[]): void {
      const sortedZones = [...zones].sort((a, b) => a.name.localeCompare(b.name))
      let orderCounter = 1

      sortedZones.forEach((zone) => {
        zone.assignedSubKrewes.forEach((assignment) => {
          assignment.paradeOrder = orderCounter++
        })
      })
    },

    /**
     * Update lieutenant assignments for a zone
     * Preserves existing assignment timestamps for lieutenants that remain
     */
    async updateLieutenants(
      year: number,
      zoneId: string,
      lieutenantIds: string[],
    ): Promise<void> {
      const lineup = this.lineups[year]
      if (!lineup) return

      // Get existing assignments for this zone to preserve timestamps
      const existingAssignments = lineup.lieutenantAssignments[zoneId] || []
      const existingMap = new Map(
        existingAssignments.map(a => [a.memberId, a.assignedAt]),
      )

      // Build new assignments, preserving timestamps for existing members
      // Note: serverTimestamp() cannot be used inside arrays, so we use Timestamp.now()
      const now = FirestoreTimestamp.now()
      const newAssignments = lieutenantIds.map(memberId => ({
        memberId,
        assignedAt: existingMap.get(memberId) || now,
      }))

      const updatedAssignments = {
        ...lineup.lieutenantAssignments,
        [zoneId]: newAssignments,
      }

      await this.saveLineup(year, { lieutenantAssignments: updatedAssignments })
    },

    /**
     * Publish or unpublish lineup
     */
    async publishLineup(year: number, publish: boolean): Promise<void> {
      const db = getFirestore()
      const lineupRef = doc(db, 'lineups', year.toString())

      await updateDoc(lineupRef, {
        publishedAt: publish ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
      })
    },

    /**
     * Toggle radio exclusion for a command staff member
     */
    async toggleRadioExclusion(year: number, memberId: string): Promise<void> {
      const lineup = this.lineups[year]
      if (!lineup) return

      const currentExclusions = lineup.radioExclusions || []
      const isExcluded = currentExclusions.includes(memberId)

      const newExclusions = isExcluded
        ? currentExclusions.filter(id => id !== memberId)
        : [...currentExclusions, memberId]

      await this.saveLineup(year, { radioExclusions: newExclusions })
    },

    /**
     * Toggle lined-up status for a subkrewe
     */
    async toggleLinedUpStatus(
      year: number,
      subkreweId: string,
      linedUp: boolean,
    ): Promise<void> {
      const db = getFirestore()
      const currentUser = useCurrentUser()
      const membersStore = useMembersStore()
      const currentMember = currentUser.value
        ? membersStore.members[`members/${currentUser.value.uid}`]
        : null

      if (!currentMember) return

      const statusRef = doc(db, 'lineups', year.toString(), 'linedUpStatus', subkreweId)

      await setDoc(statusRef, {
        subkreweId,
        linedUp,
        updatedAt: serverTimestamp(),
        updatedBy: currentMember.id,
        updatedByName: currentMember.name,
      })

      // Optimistic update
      this.$patch((state) => {
        if (!state.linedUpStatus[year]) {
          state.linedUpStatus[year] = {}
        }
        state.linedUpStatus[year][subkreweId] = {
          subkreweId,
          linedUp,
          updatedAt: new Date() as unknown as Timestamp,
          updatedBy: currentMember.id,
          updatedByName: currentMember.name,
        }
      })
    },

    /**
     * Hold a year's lineup document live (utils/liveData.ts)
     * @returns The release
     */
    subscribeToLineup(year: number): () => void {
      return holdLive(this.lineupSpec(year)).release
    },

    /**
     * Hold a year's lined-up status subcollection live
     * @returns The release
     */
    subscribeToLinedUpStatus(year: number): () => void {
      return holdLive(this.linedUpStatusSpec(year)).release
    },

    lineupSpec(year: number): LiveSpec<DocumentData> {
      const path = `lineups/${year}`
      return {
        key: path,
        listen: (emit, fail) => onSnapshot(
          doc(getFirestore(), 'lineups', year.toString()),
          ...liveDocListener(path, emit, (error) => {
            console.error(`[lineup] Couldn't load the ${year} lineup:`, error)
            fail(error)
            // After fail (stopping clears it): the page stops sketching
            this.$patch((state) => {
              state.lineupLoaded[year] = true
            })
          }),
        ),
        apply: (changes) => {
          const data = changes[0]?.data
          if (!data) return
          // Migrate old format (string[]) to new format (LieutenantAssignment[])
          const lieutenantAssignments: Record<string, { memberId: string, assignedAt: Timestamp }[]> = {}
          for (const [zoneId, assignments] of Object.entries(data.lieutenantAssignments ?? {})) {
            if (Array.isArray(assignments)) {
              lieutenantAssignments[zoneId] = assignments.map((a: string | { memberId: string, assignedAt: Timestamp }) =>
                // Old format: just the member ID
                typeof a === 'string' ? { memberId: a, assignedAt: data.updatedAt || data.createdAt } : a,
              )
            }
          }
          this.$patch((state) => {
            state.lineups[year] = {
              year,
              lineupDescription: data.lineupDescription || '',
              publishedAt: data.publishedAt || null,
              zones: data.zones || [],
              lieutenantAssignments,
              radioExclusions: data.radioExclusions || [],
              createdAt: data.createdAt,
              updatedAt: data.updatedAt,
            }
          })
        },
        evict: () => {
          this.$patch((state) => {
            delete state.lineups[year]
          })
        },
        // It has answered (it may not exist), so a page can tell "still
        // loading" from "no lineup"
        ready: () => {
          this.$patch((state) => {
            state.lineupLoaded[year] = true
          })
        },
        stopped: () => {
          this.$patch((state) => {
            delete state.lineupLoaded[year]
          })
        },
      }
    },

    linedUpStatusSpec(year: number): LiveSpec<DocumentData> {
      const collectionPath = `lineups/${year}/linedUpStatus`
      const subkreweOf = (path: string) => path.slice(collectionPath.length + 1)
      return {
        key: collectionPath,
        listen: (emit, fail) => onSnapshot(
          collection(getFirestore(), 'lineups', year.toString(), 'linedUpStatus'),
          ...liveQueryListener(collectionPath, emit, fail),
        ),
        apply: (changes) => {
          this.$patch((state) => {
            const statuses = state.linedUpStatus[year] ?? {}
            state.linedUpStatus[year] = statuses
            for (const { path, data } of changes) {
              const subkreweId = subkreweOf(path)
              statuses[subkreweId] = {
                subkreweId,
                linedUp: data!.linedUp,
                updatedAt: data!.updatedAt,
                updatedBy: data!.updatedBy,
                updatedByName: data!.updatedByName,
              }
            }
          })
        },
        evict: (paths) => {
          this.$patch((state) => {
            for (const path of paths) delete state.linedUpStatus[year]?.[subkreweOf(path)]
          })
        },
      }
    },
  },
})
